<template>
  <div>
    <DataTable
      ref="dataTable"
      :columns="columnList"
      defaultColumnOrderDir="ASC"
      :defaultColumnOrderIndex="0"
      :url="dataURL"
      :filterOptions="filterOptions"
      :hasDateRangeFilter="false"
      :useReportingServer="true"
      :displayTools="true">
      <template v-slot:title>
        <slot name="title" />
      </template>
      <template v-slot:tools>
        <slot name="tools" />
      </template>
      <template v-slot:tableHeaderCell="tableHeaderCell">
        <span v-if="tableHeaderCell.data == 8 && selectionEnabled" class="ml-2">
          <FormButton class="btn-xxs btn-default mr-1" @click="selectAllVisible">Select All</FormButton>
          <FormButton class="btn-xxs btn-default ml-1" @click="unselectAllVisible">Unselect All</FormButton>
        </span>
      </template>
      <template v-slot:row="row">
        <tr :key="row.data.id">
          <td class="table-data-nav-link">
            <LinkProductionPanel :id="row.data.id" :name="row.data.barcode" :newTab="false" />
          </td>
          <td>
            <LabelPanelStatusBadge :status="row.data.status" />
            <LabelBadge v-if="row.data.isRecut" class="ml-1 label-production-status-recut">Recut</LabelBadge>
            <LabelBadge v-if="row.data.isNCR" class="ml-1 label-order-urgent">NCR</LabelBadge>
          </td>
          <td>
            {{row.data.panelHeight}} x {{row.data.panelWidth}}
          </td>
          <td>
            {{row.data.orderItem.stock.name}}
          </td>              
          <td>
            <LinkOrder :id="row.data.order.id" :name="row.data.order.sequenceNumber" />
          </td>
          <td>
            <LinkContact :id="row.data.order.contact.id" :name="row.data.order.contact.name" />
          </td>
          <td>
            <span :class="(row.data.currentProductionStageId == productionStage.id) ? 'font-weight-bold' : ''">
              {{row.data.currentProductionStage ? row.data.currentProductionStage.name : 'None'}}
            </span>
          </td>
          <td>
            <span v-if="row.data.inventoryBin && row.data.inventoryBin.accountLocation">
              {{row.data.inventoryBin.code}} - {{row.data.inventoryBin.accountLocation.name}}
            </span>
            <span v-else>-</span>
          </td>
          <td v-if="showPendingStage || showCompletedStage">
            <span v-if="row.data.productionPanelStageTasks && row.data.productionPanelStageTasks[0] && showPendingStage">
              {{row.data.productionPanelStageTasks[0].dueDate | formatDateTime}}
            </span>
            <span v-if="row.data.productionPanelStageTasks && row.data.productionPanelStageTasks[0] && showCompletedStage">
              {{row.data.productionPanelStageTasks[0].completionDate | formatDateTime}}
            </span>
          </td>
          <td v-if="showPendingStage">
            <span v-if="row.data.productionPanelStageTasks && row.data.productionPanelStageTasks[0] && row.data.productionPanelStageTasks[0].accountLocation">
              {{row.data.productionPanelStageTasks[0].accountLocation.name}}
            </span>
          </td>
          <td class="text-right" v-if="selectionEnabled">
            <FormCheckbox :value="isPanelSelected(row.data.id)" @change="updatePanelSelection(row.data.id, $event)" />
          </td>
        </tr>
      </template>
      <template v-slot:summary="summary">
        <tr key="summary">
          <td>Totals</td>
          <td>{{summary.data.totalCount}}</td>
          <td>{{formatDecimal(summary.data.totalPanelArea, 2, true)}} m&sup2;</td>
          <td colspan="5"></td>
        </tr>
      </template>
    </DataTable>
  </div>
</template>


<script>
import { mapState } from "vuex"

import Utils from '@/utils/utils'
import LabelPanelStatusBadge from '@/modules/production/components/labelPanelStatusBadge'

import { ProductionPanelStatusTypes, ProductionPanelStatusTypeLookup } from '@/store/types'

export default {
  name: "ProductionManagerPanelDataTable",
  model: {
    event: 'update'
  },
  components: {
    LabelPanelStatusBadge
  },
  props: {
    filterType: String,
    productionStage: Object,
    showPendingStage: Boolean,
    showCompletedStage: Boolean,
    completedStartDate: String,
    completedEndDate: String,
    selectionEnabled: {
      type: Boolean,
      default: true
    },
    value: Array
  },
  watch: {
    productionStage: function() {
      this.resetSelectedPanels()
    },
    value: function() {
      this.updatedSelectedPanelMap()
    }
  },
  computed: {
    ...mapState({
      accountLocations: state => state.account.accountLocations
    }),
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
        filter: {
          '$order.isOnHold$': 0
        },
        includesummary: true
      }
      if(this.productionStage && this.showPendingStage)
        params.filter.pendingProductionStageId = this.productionStage.id
      if(this.productionStage && this.showCompletedStage) {
        params.filter.completedProductionStageId = this.productionStage.id
        if(this.completedStartDate)
          params.filter.completedStartDate = this.completedStartDate
        if(this.completedEndDate)
          params.filter.completedEndDate = this.completedEndDate
      }

      return '/production/panels?' + $.param(params)
    },
    filterOptions() {
      return _.compact([
        {
          field: 'status',
          name: 'Status',
          options: [
            { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.pending), value: ProductionPanelStatusTypes.pending, class: 'label-production-panel-status-pending' },
            { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.production), value: ProductionPanelStatusTypes.production, class: 'label-production-panel-status-production' },
            { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.recut), value: ProductionPanelStatusTypes.recut, class: 'label-production-panel-status-recut' },
            { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.despatch), value: ProductionPanelStatusTypes.despatch, class: 'label-production-panel-status-despatch' },
            { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.completed), value: ProductionPanelStatusTypes.completed, class: 'label-production-panel-status-completed' },
            { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.cancelled), value: ProductionPanelStatusTypes.cancelled, class: 'label-production-panel-status-cancelled' }
          ]
        },
        {
          field: 'isRecut',
          name: 'Recut',
          columnIndex: 1,
          options: [
            { name: 'Recut', value: 1, class: 'label-production-status-recut' },
          ]
        },
        {
          field: 'isNCR',
          name: 'NCR',
          columnIndex: 1,
          options: [
            { name: 'NCR', value: 1, class: 'label-order-urgent' },
          ]
        },
        this.showPendingStage ? 
        {
          field: 'currentProductionStageId',
          name: `Current Stage`,
          columnIndex: 1,
          options: [
            { name: `At ${this.productionStage.name}`, value: this.productionStage.id, class: 'label-production-panel-status-production' },
          ]
        } : null,
        this.showPendingStage ? 
        {
          name: `${this.productionStage.name} Location`,
          field: 'pendingAccountLocationId',
          columnIndex: 2,
          options: (this.accountLocations || []).filter(location => location.isActive).map(location => {
            return {
              name: location.name,
              value: location.id,
              class: 'label-misc'
            }
          })
        } : null
      ])
    },
    columnList() {
      return _.compact([
        { title: "Panel", data: "id" },
        { title: "Status", data: "status" },
        { title: "Size", data: "size", sortable: false },
        { title: "Stock", data: "orderItem.stock.name" },
        { title: "Order", data: "order.id" },
        { title: "Contact", data: "order.contactId" },
        { title: "Current Stage", data: "currentProductionStage.name" },
        { title: "Current Location", data: "inventoryBin.code" },
        this.showPendingStage ? { title: `Due At ${this.productionStage.name}`, data: "productionPanelStageTask.dueDate", dateFilter: false, type: 'datetime' } : null,
        this.showPendingStage ? { title: `${this.productionStage.name} Location`, data: "productionPanelStageTask.accountLocation.name" } : null,
        this.showCompletedStage ? { title: `Completed ${this.productionStage.name}`, data: "productionPanelStageTask.completedDate", dateFilter: false, type: 'datetime' } : null,
        this.selectionEnabled ? { title: "Selection", data: "", sortable: false, export: false } : null,
      ])
    }
  },
  data() {
    return {
      selectedPanelMap: {}
    }
  },
  mounted() {
    this.updatedSelectedPanelMap()

    // Apply default filters
    this.$nextTick(() => {
      if(this.showPendingStage) {
        let defaultFilters = _.compact([
          { value: ProductionPanelStatusTypes.production, field: 'status' },
          { value: ProductionPanelStatusTypes.pending, field: 'status' }
        ])

        this.$refs.dataTable.applySelectedFilters(defaultFilters)
      }
    })
  },
  methods: {
    reload() {
      this.$refs.dataTable.reload()
    },
    isPanelSelected(panelId) {
      return this.selectedPanelMap[panelId]
    },
    updatePanelSelection(panelId, selected) {
      this.selectedPanelMap[panelId] = selected
      this.updatePanelList()
    },
    updatedSelectedPanelMap() {
      this.selectedPanelMap = {}
      if(this.value) {
        _.uniq(this.value).forEach(panelId => {
          this.selectedPanelMap[panelId] = true
        })
      }
    },
    selectAllVisible() {
      this.$refs.dataTable.getRowListData().forEach(panel => {
        this.$set(this.selectedPanelMap, panel.id, true)
      })
      this.updatePanelList()
    },
    unselectAllVisible() {
      this.$refs.dataTable.getRowListData().forEach(panel => {
        this.$set(this.selectedPanelMap, panel.id, false)
      })
      this.updatePanelList()
    },
    updatePanelList() {
      this.$emit('update', Object.keys(this.selectedPanelMap).filter(panelId => this.selectedPanelMap[panelId]).map(panelId => parseInt(panelId)))
    },
    resetSelectedPanels() {
      this.selectedPanelMap = {}
    }
  }
};
</script>
